// Phase 44 — GC bid-list mining.
//
// Substrate Zod schemas for the gc-award scrapers + queue consumer. Mirrors
// the shape of OpportunityCandidateSchema but tightened around award-page
// data: gc_name_raw is required (no award without a contractor), and we have
// award-specific fields like awarded_at + project_value_usd + scope_summary.

import { z } from 'zod';
import { DisciplineSchema } from './common';

export const GcAwardCandidateSchema = z.object({
  // Per-source unique key. Stable across re-crawls of the same award row.
  // Typically the agency's contract number or a (date|title) hash.
  externalId: z.string().min(1).max(500),
  // ISO date (YYYY-MM-DD). Scrapers normalize before emitting.
  awardedAt: z.string().max(40).optional(),
  projectTitle: z.string().min(3).max(500),
  // USD integer (round dollars). null/undefined when agency doesn't publish.
  projectValueUsd: z.number().int().nonnegative().max(1e10).optional(),
  scopeSummary: z.string().max(20_000).optional(),
  // The GC name verbatim from the scrape — required. Canonicalization
  // happens downstream in services/gc-canonicalize.ts.
  gcNameRaw: z.string().min(2).max(300),
  agencyName: z.string().max(300).optional(),
  locationCity: z.string().max(80).optional(),
  locationState: z.string().min(2).max(8).optional(),
  // Pre-tagged disciplines if the scraper has cheap signal (e.g. scope text
  // already mentions "fire alarm"). Queue consumer runs tagDisciplines()
  // anyway, so this is purely additive.
  disciplines: z.array(DisciplineSchema).optional(),
  sourcePageUrl: z.string().url(),
  detailUrl: z.string().url().optional(),
  rawContent: z.unknown().optional(),
});
export type GcAwardCandidate = z.infer<typeof GcAwardCandidateSchema>;
